/**
 * 2.0.0: 语言切换 — 简洁小方框（中/あ/e），点击展开下拉选择
 *
 * 保持与主题按钮一致的小方框样式，点击后下拉显示：中文 / 日本語 / English。
 * 悬浮时橙色高亮。
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type LangCode = 'zh' | 'ja' | 'en';

const OPTIONS: { code: LangCode; label: string }[] = [
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'en', label: 'English' },
];

const currentLangCode = (lang: string): LangCode => {
  if (lang.startsWith('zh')) return 'zh';
  if (lang.startsWith('ja')) return 'ja';
  return 'en';
};

const currentShortLabel = (code: LangCode): string => {
  return code === 'zh' ? '中' : code === 'ja' ? 'あ' : 'e';
};

export const LanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', onOutside);
      return () => document.removeEventListener('mousedown', onOutside);
    }
  }, [open]);

  const current = currentLangCode(i18n.language);
  const currentLabel = OPTIONS.find((o) => o.code === current)?.label ?? 'English';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={currentLabel}
        aria-label={`${t('switchLanguage', '切换语言')}: ${currentLabel}`}
        aria-expanded={open}
        className="
          w-8 h-8 border border-gray-300 dark:border-gray-600 rounded-md
          flex items-center justify-center text-sm font-medium
          text-gray-500 dark:text-gray-400
          hover:text-orange-600 dark:hover:text-orange-400
          hover:border-orange-400 dark:hover:border-orange-500
          transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 dark:focus:ring-offset-gray-950
        "
      >
        {currentShortLabel(current)}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 py-1 min-w-[120px] rounded-lg
                     bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700
                     shadow-lg dark:shadow-xl z-50"
        >
          {OPTIONS.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              onClick={() => {
                i18n.changeLanguage(code);
                setOpen(false);
              }}
              className={`
                w-full px-3 py-2 text-sm text-left
                transition-colors duration-200
                hover:text-orange-600 dark:hover:text-orange-400
                hover:bg-orange-50 dark:hover:bg-orange-900/10
                ${current === code ? 'text-orange-600 dark:text-orange-400 bg-orange-50/50 dark:bg-orange-900/20' : 'text-gray-700 dark:text-gray-300'}
              `}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
